import { ALL_PRODUCT_IDS } from '../subscriptions/catalog';
import type { ProductId } from '../subscriptions/types';
import { loadNativeWebViewInsets, parseNativeAppOS } from './nativeWebViewInsets';
import { isNativeWebView } from './nativeAuthBridge';

export const NATIVE_APP_RESUMED_MESSAGE_TYPE = 'NATIVE_APP_RESUMED';

const PENDING_SESSION_ID_KEY = 'pendingCheckoutSessionId';
const PENDING_PRODUCT_ID_KEY = 'pendingCheckoutProductId';
const PENDING_ASSIGNMENT_ID_KEY = 'pendingCheckoutAssignmentId';
const PENDING_STARTED_AT_KEY = 'pendingCheckoutStartedAt';

export type PendingCheckoutContext = {
  sessionId: string;
  productId: ProductId;
  assignmentId?: number;
  startedAt: number;
};

type NativeAppResumedListener = () => void;

const resumedListeners = new Set<NativeAppResumedListener>();
let messageListenerRegistered = false;

export type ExternalPurchaseOpenStatus =
  | 'openedStoreKitLink'
  | 'openedFallbackBrowser'
  | 'unavailable'
  | 'cancelled'
  | 'failed';

export type ExternalPurchaseOpenResult = {
  status: ExternalPurchaseOpenStatus;
  usedStoreKit?: boolean;
  message?: string;
};

export function isIosNativeApp(): boolean {
  if (!isNativeWebView()) {
    return false;
  }
  const appOS = loadNativeWebViewInsets().appOS ?? readLocalAppOS();
  return appOS === 'ios';
}

function readLocalAppOS() {
  if (typeof window === 'undefined') {
    return undefined;
  }
  try {
    return parseNativeAppOS(window.localStorage.getItem('appOS'));
  } catch {
    return undefined;
  }
}

function parseProductId(value: string | null): ProductId | null {
  if (!value || !ALL_PRODUCT_IDS.includes(value as ProductId)) {
    return null;
  }
  return value as ProductId;
}

export function savePendingCheckout(context: {
  sessionId: string;
  productId: ProductId;
  assignmentId?: number;
}): void {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.localStorage.setItem(PENDING_SESSION_ID_KEY, context.sessionId);
    window.localStorage.setItem(PENDING_PRODUCT_ID_KEY, context.productId);
    if (context.assignmentId !== undefined) {
      window.localStorage.setItem(PENDING_ASSIGNMENT_ID_KEY, String(context.assignmentId));
    } else {
      window.localStorage.removeItem(PENDING_ASSIGNMENT_ID_KEY);
    }
    window.localStorage.setItem(PENDING_STARTED_AT_KEY, String(Date.now()));
  } catch {
    // ignore storage failures
  }
}

export function readPendingCheckout(): PendingCheckoutContext | null {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    const sessionId = window.localStorage.getItem(PENDING_SESSION_ID_KEY);
    const productId = parseProductId(window.localStorage.getItem(PENDING_PRODUCT_ID_KEY));
    const startedAtRaw = window.localStorage.getItem(PENDING_STARTED_AT_KEY);
    if (!sessionId || !productId || !startedAtRaw) {
      return null;
    }
    const assignmentRaw = window.localStorage.getItem(PENDING_ASSIGNMENT_ID_KEY);
    const assignmentId =
      assignmentRaw && assignmentRaw !== '' ? parseInt(assignmentRaw, 10) : undefined;
    const startedAt = parseInt(startedAtRaw, 10);
    if (Number.isNaN(startedAt)) {
      return null;
    }
    return {
      sessionId,
      productId,
      ...(assignmentId !== undefined && !Number.isNaN(assignmentId) ? { assignmentId } : {}),
      startedAt,
    };
  } catch {
    return null;
  }
}

export function clearPendingCheckout(): void {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.localStorage.removeItem(PENDING_SESSION_ID_KEY);
    window.localStorage.removeItem(PENDING_PRODUCT_ID_KEY);
    window.localStorage.removeItem(PENDING_ASSIGNMENT_ID_KEY);
    window.localStorage.removeItem(PENDING_STARTED_AT_KEY);
  } catch {
    // ignore storage failures
  }
}

export function subscribeNativeAppResumed(listener: NativeAppResumedListener): () => void {
  resumedListeners.add(listener);
  return () => {
    resumedListeners.delete(listener);
  };
}

function notifyNativeAppResumed(): void {
  resumedListeners.forEach((listener) => listener());
}

function onWindowMessage(event: MessageEvent): void {
  const data = event.data;
  if (!data || typeof data !== 'object') {
    return;
  }
  if ((data as { type?: string }).type === NATIVE_APP_RESUMED_MESSAGE_TYPE) {
    notifyNativeAppResumed();
  }
}

export function bootstrapNativeCheckoutBridge(): void {
  if (typeof window === 'undefined' || messageListenerRegistered) {
    return;
  }
  window.addEventListener('message', onWindowMessage);
  messageListenerRegistered = true;
}

export async function canOpenStoreKitExternalPurchase(): Promise<boolean> {
  if (window.flutter_inappwebview?.callHandler) {
    const result = await window.flutter_inappwebview.callHandler(
      'canOpenStoreKitExternalPurchase',
    );
    return Boolean(result);
  }
  return false;
}

function normalizeExternalPurchaseResult(
  result: unknown,
): ExternalPurchaseOpenResult {
  if (!result || typeof result !== 'object') {
    return { status: 'failed' };
  }
  const status = (result as { status?: string }).status;
  if (
    status === 'openedStoreKitLink' ||
    status === 'openedFallbackBrowser' ||
    status === 'unavailable' ||
    status === 'cancelled' ||
    status === 'failed'
  ) {
    return {
      status,
      usedStoreKit: Boolean((result as { usedStoreKit?: boolean }).usedStoreKit),
      message:
        typeof (result as { message?: string }).message === 'string'
          ? (result as { message?: string }).message
          : undefined,
    };
  }
  return { status: 'failed' };
}

export async function openStoreKitExternalPurchase(
  checkoutUrl: string,
): Promise<ExternalPurchaseOpenResult> {
  if (window.flutter_inappwebview?.callHandler) {
    const result = await window.flutter_inappwebview.callHandler(
      'openStoreKitExternalPurchase',
      checkoutUrl,
    );
    return normalizeExternalPurchaseResult(result);
  }
  window.open(checkoutUrl, '_blank', 'noopener,noreferrer');
  return { status: 'openedFallbackBrowser', usedStoreKit: false };
}

export async function startExternalCheckout(
  checkoutUrl: string,
): Promise<ExternalPurchaseOpenResult> {
  if (isIosNativeApp()) {
    return openStoreKitExternalPurchase(checkoutUrl);
  }
  if (window.flutter_inappwebview?.callHandler) {
    await window.flutter_inappwebview.callHandler('startExternalCheckout', checkoutUrl);
    return { status: 'openedFallbackBrowser', usedStoreKit: false };
  }
  window.open(checkoutUrl, '_blank', 'noopener,noreferrer');
  return { status: 'openedFallbackBrowser', usedStoreKit: false };
}
